import type { KeycloakConfig, KeycloakInitOptions, KeycloakLoginOptions, KeycloakLogoutOptions } from 'keycloak-js';

import Keycloak from 'keycloak-js';
import { withoutBase, withoutHost } from 'ufo';
import { type Router } from 'vue-router';

import { createStorage } from '@abaninja/util-storage';

export const token = createStorage('kc-token');

export const refreshToken = createStorage('kc-refreshToken');

export let keycloak: Keycloak | undefined;

const saveTokensToStorage = () => {
  token.set(keycloak?.token);
  refreshToken.set(keycloak?.refreshToken);
};

const deleteTokens = () => {
  token.delete();
  refreshToken.delete();
};

export const locationSessionStorageKey = 'requestedLocation';

let keycloakInterval: NodeJS.Timeout | null = null;
const removeKeycloakRefreshInterval = () => {
  if (keycloakInterval) {
    clearInterval(keycloakInterval);
  }
};

async function refreshCb() {
  try {
    const isTokenUpdated = await keycloak?.updateToken(70);

    if (isTokenUpdated) {
      // setToken
      saveTokensToStorage();
    }
  } catch (error) {
    console.error(error);
  }
}

const createKeycloakRefreshInterval = () => {
  removeKeycloakRefreshInterval();

  return setInterval(() => void refreshCb(), 3000);
};

const initKeycloak = (
  keycloakConfig: KeycloakConfig,
  keycloakInitOptions: KeycloakInitOptions,
  keycloakLoginOptions: KeycloakLoginOptions,
  // eslint-disable-next-line complexity
) => async (createApp: () => void, router: Router) => {
  try {
    const route = router.resolve(withoutBase(withoutHost(window.location.href), router.options.history.base));

    // persist current route
    if (!route.meta.dontSaveRedirect) {
      sessionStorage.setItem(locationSessionStorageKey, JSON.stringify(window.location));
    }

    const authenticated = await keycloak?.init(keycloakInitOptions);

    if (route.meta.public) {
      createApp();

      return;
    }

    if (!authenticated) {
      await keycloak?.login(keycloakLoginOptions);

      return;
    }

    // setToken
    saveTokensToStorage();

    createApp();

    // set interval
    keycloakInterval = createKeycloakRefreshInterval();
  } catch {
    deleteTokens();
    await keycloak?.login(keycloakLoginOptions);
  }
};

let loginOptions: KeycloakLoginOptions;
let logoutOptions: KeycloakLogoutOptions;

export const createKeycloak = (
  keycloakConfig: KeycloakConfig,
  keycloakInitOptions: KeycloakInitOptions,
  keycloakLoginOptions: KeycloakLoginOptions,
  keycloakLogoutOptions: KeycloakLogoutOptions,
) => {
  keycloak = new Keycloak(keycloakConfig);
  loginOptions = keycloakLoginOptions;
  logoutOptions = keycloakLogoutOptions;

  return initKeycloak(keycloakConfig, keycloakInitOptions, keycloakLoginOptions);
};

export const logout = () => {
  deleteTokens();
  void keycloak?.logout(logoutOptions);
};

export const login = () => {
  deleteTokens();
  void keycloak?.login(loginOptions);
};

export const createLoginUrl = () => keycloak?.createLoginUrl(loginOptions);

export const createRegisterUrl = () => keycloak?.createRegisterUrl(loginOptions);

export const createAccountUrl = () => keycloak?.createAccountUrl(loginOptions);
